// Wrap in IIFE to avoid namespace collisions (Shopify best practice)
(function() {
  'use strict';

  if (!customElements.get('testimonial-lightbox')) {
    customElements.define(
      'testimonial-lightbox',
      class TestimonialLightbox extends HTMLElement {
      constructor() {
        super();
        this.swiperInstance = null;
        this.currentIndex = 0;
        this.images = [];
      }

      connectedCallback() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            this.init();
          });
        } else {
          this.init();
        }
      }

      init() {
        // Collect all images from the section
        this.collectImages();
        // Initialize click handlers using event delegation
        this.initClickHandlers();
      }

      collectImages() {
        const section = this.closest('[id^="section--"]');
        if (!section) return;

        this.images = [];
        const imageElements = section.querySelectorAll('.testimonial-lightbox-trigger');
        
        imageElements.forEach((trigger, index) => {
          const imgElement = trigger.querySelector('img');
          const imgSrc = trigger.dataset.fullImage || (imgElement ? imgElement.src : '');
          const imgAlt = trigger.dataset.alt || (imgElement ? imgElement.alt : '') || '';
          const blockId = trigger.dataset.blockId || '';
          
          if (imgSrc) {
            this.images.push({
              src: imgSrc,
              alt: imgAlt,
              blockId: blockId,
              index: index
            });
          }
        });
      }

      initClickHandlers() {
        const section = this.closest('[id^="section--"]');
        if (!section) return;

        // Cache triggers list once for better performance
        const triggers = section.querySelectorAll('.testimonial-lightbox-trigger');
        const triggersArray = Array.from(triggers);

        // Use event delegation for better performance
        this.clickHandler = (e) => {
          const trigger = e.target.closest('.testimonial-lightbox-trigger');
          if (!trigger) return;
          
          // Support both click and keyboard (Enter/Space) - Shopify accessibility requirement
          if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') {
            return;
          }
          
          e.preventDefault();
          e.stopPropagation();
          
          // Use cached array for better performance
          const index = triggersArray.indexOf(trigger);
          
          if (index >= 0) {
            this.openLightbox(index, e);
          }
        };
        
        section.addEventListener('click', this.clickHandler, { passive: false });
        section.addEventListener('keydown', this.clickHandler, { passive: false });
        
        // Set up triggers for accessibility (Shopify accessibility best practices)
        triggers.forEach((trigger, index) => {
          trigger.style.cursor = 'pointer';
          if (!trigger.hasAttribute('tabindex')) {
            trigger.setAttribute('tabindex', '0');
          }
          trigger.setAttribute('role', 'button');
          const img = trigger.querySelector('img');
          const imgAlt = trigger.dataset.alt || (img ? img.alt : '') || '';
          trigger.setAttribute('aria-label', imgAlt ? `Open ${imgAlt} in lightbox` : `Open image ${index + 1} in lightbox`);
        });
      }

      disconnectedCallback() {
        // Clean up event listeners
        const section = this.closest('[id^="section--"]');
        if (section && this.clickHandler) {
          section.removeEventListener('click', this.clickHandler);
          section.removeEventListener('keydown', this.clickHandler);
        }
      }

      openLightbox(index, event = null) {
        if (this.images.length === 0) {
          this.collectImages();
        }

        if (this.images.length === 0) return;

        this.currentIndex = index >= 0 && index < this.images.length ? index : 0;
        
        // Create or get modal
        let modal = document.querySelector('testimonial-lightbox-modal');
        if (!modal) {
          modal = document.createElement('testimonial-lightbox-modal');
          document.body.appendChild(modal);
        }

        modal.setImages(this.images);
        modal.show(this.currentIndex, event?.target || null);
      }
    }
  );
}

  if (!customElements.get('testimonial-lightbox-modal')) {
    customElements.define(
      'testimonial-lightbox-modal',
      class TestimonialLightboxModal extends HTMLElement {
      constructor() {
        super();
        this.swiperInstance = null;
        this.images = [];
        this.currentIndex = 0;
        this.openedBy = null;
      }

      connectedCallback() {
        this.innerHTML = `
          <div class="testimonial-lightbox-overlay" role="dialog" aria-modal="true" aria-label="Image gallery">
            <button class="testimonial-lightbox-close" id="TestimonialLightboxClose" aria-label="Close image gallery" type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <div class="testimonial-lightbox-container">
              <div class="testimonial-lightbox-swiper swiper">
                <div class="swiper-wrapper"></div>
                <div class="swiper-button-next" aria-label="Next image"></div>
                <div class="swiper-button-prev" aria-label="Previous image"></div>
                <div class="swiper-pagination" aria-label="Image pagination"></div>
              </div>
            </div>
          </div>
        `;

        // Cache DOM queries
        this.closeButton = this.querySelector('#TestimonialLightboxClose');
        this.swiperContainer = this.querySelector('.testimonial-lightbox-swiper');
        this.swiperWrapper = this.querySelector('.swiper-wrapper');
        this.dialog = this.querySelector('[role="dialog"]');
        this.overlay = this.querySelector('.testimonial-lightbox-overlay');

        // Bind event handlers for proper cleanup
        this.handleClose = () => this.hide();
        this.handleClick = (e) => {
          if (e.target === this.overlay) {
            this.hide();
          }
        };
        this.handleKeyUp = (e) => {
          // Escape key support (Shopify accessibility requirement)
          if (e.code.toUpperCase() === 'ESCAPE' || e.key === 'Escape') {
            // If zoomed in, zoom out first, otherwise close
            if (this.swiperInstance && this.swiperInstance.zoom) {
              const currentSlide = this.swiperInstance.slides[this.swiperInstance.activeIndex];
              if (currentSlide && currentSlide.classList.contains('swiper-slide-zoomed')) {
                this.swiperInstance.zoom.out();
                e.preventDefault();
                e.stopPropagation();
                return;
              }
            }
            this.hide();
            e.preventDefault();
            e.stopPropagation();
          }
        };
        this.handleKeyDown = (e) => {
          if (!this.swiperInstance || !this.swiperInstance.zoom) return;
          
          if (e.key === '+' || e.key === '=') {
            e.preventDefault();
            this.swiperInstance.zoom.in();
          } else if (e.key === '-' || e.key === '_') {
            e.preventDefault();
            this.swiperInstance.zoom.out();
          }
        };

        this.closeButton.addEventListener('click', this.handleClose, { passive: true });
        this.addEventListener('click', this.handleClick, { passive: true });
        this.addEventListener('keyup', this.handleKeyUp, { passive: false });
        this.addEventListener('keydown', this.handleKeyDown, { passive: false });
      }

      disconnectedCallback() {
        // Clean up event listeners
        if (this.closeButton) {
          this.closeButton.removeEventListener('click', this.handleClose);
        }
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keyup', this.handleKeyUp);
        this.removeEventListener('keydown', this.handleKeyDown);
        
        // Clean up Swiper instance
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }
      }

      setImages(images) {
        this.images = images;
        this.renderImages();
      }

      renderImages() {
        if (!this.swiperWrapper) return;

        // Use DocumentFragment for better performance
        const fragment = document.createDocumentFragment();
        
        this.images.forEach((image, index) => {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide';
          
          const slideContent = document.createElement('div');
          slideContent.className = 'testimonial-lightbox-slide';
          
          const zoomContainer = document.createElement('div');
          zoomContainer.className = 'swiper-zoom-container';
          
          const imageWrapper = document.createElement('div');
          imageWrapper.className = 'testimonial-lightbox-image-wrapper';
          
          const img = document.createElement('img');
          img.src = image.src;
          img.alt = image.alt || '';
          img.loading = 'eager';
          img.className = 'testimonial-lightbox-image';
          img.setAttribute('role', 'img');
          img.setAttribute('aria-label', image.alt || `Image ${index + 1}`);
          
          imageWrapper.appendChild(img);
          zoomContainer.appendChild(imageWrapper);
          slideContent.appendChild(zoomContainer);
          slide.appendChild(slideContent);
          fragment.appendChild(slide);
        });
        
        this.swiperWrapper.innerHTML = '';
        this.swiperWrapper.appendChild(fragment);
      }

      show(index = 0, opener = null) {
        this.openedBy = opener;
        this.currentIndex = index >= 0 && index < this.images.length ? index : 0;
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        
        // Ensure images are rendered before initializing Swiper
        if (this.images.length > 0 && this.swiperWrapper) {
          // Only render if wrapper is empty
          if (this.swiperWrapper.children.length === 0) {
            this.renderImages();
          }
        }
        
        // Initialize or update Swiper
        this.initSwiper();
        
        // Focus trap using Shopify's native function
        if (typeof trapFocus === 'function') {
          trapFocus(this, this.dialog);
        } else {
          this.closeButton.focus();
        }
        
        // Pause all media (Shopify best practice)
        if (window.pauseAllMedia) {
          window.pauseAllMedia();
        }
      }

      initSwiper() {
        // Wait for Swiper to be available - use requestAnimationFrame for better performance
        if (!window.Swiper) {
          // Retry after a short delay with cleanup
          if (this.swiperTimeout) {
            clearTimeout(this.swiperTimeout);
          }
          this.swiperTimeout = setTimeout(() => {
            this.swiperTimeout = null;
            if (window.Swiper) {
              requestAnimationFrame(() => this.initSwiper());
            } else {
              console.error('Swiper.js is not loaded. Please ensure Swiper is loaded before using the lightbox.');
            }
          }, 100);
          return;
        }

        if (this.swiperInstance) {
          this.swiperInstance.slideTo(this.currentIndex, 0);
          return;
        }

        // Ensure we have slides before initializing
        if (!this.swiperWrapper || this.swiperWrapper.children.length === 0) {
          console.warn('No slides found for lightbox');
          return;
        }

        // Cache navigation and pagination elements
        const nextButton = this.querySelector('.swiper-button-next');
        const prevButton = this.querySelector('.swiper-button-prev');
        const pagination = this.querySelector('.swiper-pagination');

        // Bind handlers for proper cleanup
        this.handleSlideChange = () => {
          // Update focus for accessibility
          if (this.closeButton) {
            this.closeButton.focus();
          }
          // Reset zoom on slide change
          if (this.swiperInstance && this.swiperInstance.zoom) {
            this.swiperInstance.zoom.out();
          }
        };

        this.handleZoomChange = (swiper, scale) => {
          // Zoom change handler - no indicator needed
        };

        this.swiperInstance = new Swiper(this.swiperContainer, {
          initialSlide: this.currentIndex,
          loop: false,
          speed: 600,
          effect: 'slide',
          slidesPerView: 1,
          slidesPerGroup: 1,
          spaceBetween: 0,
          observer: true,
          observeParents: true,
          keyboard: {
            enabled: true,
          },
          navigation: {
            nextEl: nextButton,
            prevEl: prevButton,
          },
          pagination: {
            el: pagination,
            type: 'fraction',
            clickable: true,
          },
          zoom: {
            maxRatio: 3,
            minRatio: 1,
            toggle: true,
            containerClass: 'swiper-zoom-container',
          },
          touchEventsTarget: 'container',
          simulateTouch: true,
          allowTouchMove: true,
          resistance: true,
          resistanceRatio: 0.85,
          watchSlidesProgress: true,
          watchSlidesVisibility: true,
          on: {
            slideChange: this.handleSlideChange,
            zoomChange: this.handleZoomChange,
          }
        });

      }

      hide() {
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        
        // Clean up timeout if exists
        if (this.swiperTimeout) {
          clearTimeout(this.swiperTimeout);
          this.swiperTimeout = null;
        }
        
        // Remove focus trap using Shopify's native function
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.openedBy);
        }
        
        // Pause all media (Shopify best practice)
        if (window.pauseAllMedia) {
          window.pauseAllMedia();
        }
        
        // Dispatch modal closed event (Shopify pattern)
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }
      }
    }
  );
  }
})();

